import { useEffect, useRef, useState } from 'react';

/**
 * Smart auto-refresh hook that pauses during user interactions
 * Prevents interrupting scrolling, typing, or other user activities
 * 
 * @param {Function} callback - Function to call on refresh
 * @param {number} interval - Refresh interval in milliseconds (default: 30000)
 * @param {number} inactivityDelay - How long to wait after user activity before resuming refresh (default: 5000ms)
 */
export function useSmartRefresh(callback, interval = 30000, inactivityDelay = 5000) {
  const [isUserActive, setIsUserActive] = useState(false);
  const inactivityTimerRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    // Track user activity
    const handleUserActivity = () => {
      setIsUserActive(true);

      // Clear existing inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      // Set new inactivity timer
      inactivityTimerRef.current = setTimeout(() => {
        setIsUserActive(false);
      }, inactivityDelay);
    };

    // Scroll detection with debounce
    const handleScroll = () => {
      setIsUserActive(true);
      
      // Clear existing scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce: mark as inactive after scroll stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserActive(false);
      }, inactivityDelay);
    };

    // Check if user is typing
    const isTyping = () => {
      const activeElement = document.activeElement;
      return activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
    };

    // Enhanced check before refresh
    const shouldRefresh = () => {
      return !isUserActive && !isTyping();
    };

    // Set up auto-refresh interval
    const startRefresh = () => {
      refreshIntervalRef.current = setInterval(() => {
        if (shouldRefresh()) {
          try {
            callback();
          } catch (error) {
            console.error('Error during auto-refresh:', error);
          }
        }
      }, interval);
    };

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // Scroll listener (with debounce)
    window.addEventListener('scroll', handleScroll, true); // Capture phase to catch all scrolls

    // Start refresh interval
    startRefresh();

    // Cleanup
    return () => {
      // Clear all timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Remove event listeners
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [callback, interval, inactivityDelay, isUserActive]);

  // Return manual refresh function and active state
  return {
    isUserActive,
    manualRefresh: callback
  };
}
